// Single source of truth for pricing display data (pricing v2 — docs/pricing-v2.md).
// Page quotas are proposed pending final sign-off: change them HERE only.
// Polar product IDs live in env vars, not here.

export const TIERS = [
  {
    id: 'free',
    name: 'Free',
    monthly: 0,
    annual: null,
    pages: 5,  // evaluation tier — enough to judge extraction quality
    features: [
      'All document types',
      'Fields, tables, line items & notes',
      'Quality insights & confidence scores',
      'JSON & CSV export',
      'Email-in · 30-day retention',
    ],
    cta: 'Start free',
    featured: false,
  },
  {
    id: 'solo',
    name: 'Solo',
    monthly: 9,
    annual: 90,
    pages: 200,
    features: [
      'Everything in Free',
      'Pay-as-you-go overage — you set the budget',
      'Annual billing — 2 months free',
    ],
    cta: 'Choose Solo',
    featured: false,
  },
  // NOTE: feature strings here are the single source for PricingView AND
  // the in-app UpgradePrompt modal — keep them cumulative and truthful.
  {
    id: 'business',
    name: 'Business',
    monthly: 29,
    annual: 290,
    pages: 800,
    features: [
      'Everything in Solo',
      'Custom extraction fields',
      'Bulk export',
      'Email support',
    ],
    cta: 'Choose Business',
    featured: true,
  },
  // Enterprise replaced the Team tier (2026-07-19): seats + API are the
  // differentiators. 5 users included; +5-user blocks add 1,000 pooled pages.
  {
    id: 'enterprise',
    name: 'Enterprise',
    monthly: 99,
    annual: 990,
    pages: 2400,
    features: [
      'Everything in Business',
      '5 users — 1 admin + 4 members',
      '2,400 pooled pages across your team',
      'Add 5-user blocks (+1,000 pages) · $35/mo',
      'Team-shared custom fields',
      'API access',
      '365-day document retention',
    ],
    cta: 'Choose Enterprise',
    featured: false,
  },
]

// The custom escape hatch ABOVE self-serve Enterprise: SSO, DPA, BYOC.
export const ENTERPRISE = {
  name: 'Enterprise Custom',
  body: 'Need SSO, a DPA, your own AWS account, or more than 15 users? Custom page quotas and invoicing.',
  cta: 'Contact us',
  href: 'mailto:hello@papersnap.io?subject=Papersnap Enterprise Custom',
}

// Pricing v3: consent-gated metered overage replaced credit packs
// (2026-06-11). Cents per page beyond the plan quota; sliding by tier.
// Off by default — users opt in by setting a monthly budget.
// `team` retained as a legacy alias through the team→enterprise rename.
export const OVERAGE_CENTS = { free: null, solo: 6, business: 5, team: 4, enterprise: 4 }

export function overageRateCents(tierId) {
  return OVERAGE_CENTS[tierId] ?? null
}

export const FREE_PAGES = TIERS[0].pages

// Display helpers — keep all price math in this file so the toggle renders
// identically everywhere (PricingView, UpgradePrompt, ProShowcaseModal).
export function priceFor(tier, interval = 'month') {
  if (tier.monthly === 0) return { amount: '$0', per: '', note: '' }
  if (interval === 'year' && tier.annual !== null) {
    return {
      amount: `$${(tier.annual / 12).toFixed(2).replace(/\.00$/, '')}`,
      per: '/mo',
      note: `billed $${tier.annual}/year`,
    }
  }
  return { amount: `$${tier.monthly}`, per: '/mo', note: '' }
}

export function perPageCents(tier, interval = 'month') {
  if (!tier.pages || tier.monthly === 0) return null
  const monthlyCost = interval === 'year' && tier.annual !== null ? tier.annual / 12 : tier.monthly
  return ((monthlyCost / tier.pages) * 100).toFixed(1)
}

// Annual savings vs month-to-month (10x vs 12x monthly = ~17%).
export function annualSavingsPct(tier) {
  if (!tier.annual || !tier.monthly) return null
  return Math.round((1 - tier.annual / (tier.monthly * 12)) * 100)
}
